import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { debounce } from 'lodash';
import { comparer, reaction } from 'mobx';
import { useEffect, useRef } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { TranslationStore } from '@src/stores-v2/TranslationStore';

const logger = LogManager.getLogger(
  'hook:useReproduceCaptionsOnTranslationEdit',
);

// Coalesce bursts of edits (and the optimistic-fold + debounced-save double
// mutation of a single edit) into one re-produce. Kept short so the rendered
// captions still feel responsive.
const REPRODUCE_DEBOUNCE_MS = 300;

type Handlers = {
  onReproduced: () => void;
  onPersist?: () => void;
};

/** A mounted caller. The ref lets it re-render without re-installing. */
type Subscriber = { current: Handlers };

type Installation = {
  /** Registration order; the first entry drives the callbacks. */
  subscribers: Subscriber[];
  teardown: () => void;
};

/**
 * PLAT-10945: exactly ONE installation per editor, shared by every component
 * that mounts this hook against that editor.
 *
 * Why: several components legitimately mount the same hydration hook against the
 * same story editor — Creator Studio alone does it twice (`LeftSidePanel` and
 * `RightSidePanel` → `KaraokeSettings/CaptionsLanguageHeaderToggle`), and
 * Content Studio keeps its Transcript & Speakers tab and its Captions tab both
 * mounted at once. Installing the reaction per mount meant one correction ran
 * the re-produce once per mount AND called the host's save once per mount.
 * `VideoCreatorStore.saveCurrentAndParentVideos` queues a concurrent request
 * rather than dropping it, so every one of those calls completed: measured in
 * the running application on 2026-07-30, one corrected word wrote two caption
 * tracks and issued two story publishes.
 *
 * Keyed by the editor, not by the surface, because "one save" is a property of
 * the editor being saved. Two different editors (the story editor and the clip
 * preview's own editor, both live while the clip modal is open) keep their own
 * installations and their own saves — that is correct, they save different
 * videos.
 *
 * A `WeakMap` so a discarded editor takes its entry with it.
 */
const installations = new WeakMap<IVideoEditor, Installation>();

/** Build the editor's one reaction. Called by the first subscriber only. */
function installReaction(
  editor: IVideoEditor,
  translationStore: TranslationStore,
): Installation {
  const subscribers: Subscriber[] = [];

  const reproduce = debounce(() => {
    // The first-registered live mount drives the callbacks. Every mount against
    // one editor passes behaviourally identical ones — the five story-level
    // hosts all go through `useTranslationStoreHydration`, which builds them
    // from the same singleton `VideoCreatorStore` — so which mount wins changes
    // nothing except how many times the work runs.
    const owner = subscribers[0]?.current;
    if (!owner) return;
    const lang = editor.getDefaultKaraokeConfig()?.language;
    if (!lang || isOriginalLang(lang)) return;
    // Don't create a track as a side effect — only refresh an existing one.
    if (!editor.getKaraokeTrack()) return;
    try {
      // TEXT ONLY, never `commandProduceKaraoke`: a re-produce rebuilds the
      // track from the producer's segmentation and silently discards the user's
      // caption-structure edits (synthetic line breaks, merged rows — a merge
      // leaves no record, so nothing can replay it). See the hook's docblock.
      const synced = editor.commandSyncTranslatedCaptionText(false);
      if (synced) {
        owner.onReproduced();
        // Persist only once this language has no edit still waiting to be
        // written to the translation record. The single-word popover path
        // mutates the store twice for one correction — the optimistic pending
        // edit, then the saved record ~4 s later when the POST returns — and
        // both re-produce. Measured on the real stack, so no debounce window
        // can reliably coalesce them; gating on the pending set collapses that
        // pair into one save.
        //
        // What this gate does NOT do, so nobody relies on a protection that
        // isn't here:
        //   - It does not make a failed translation save safe. The span
        //     correction path (`TranslationStore.createOverrideSegment`) never
        //     writes `pendingEdits` at all — it only removes entries — and
        //     `saveTranslationData` logs its error and swallows it. So on the
        //     path this ticket exists for the gate is always open, and a
        //     caption track can be stored from a translation record whose own
        //     upload later failed. Closing that needs the store to report the
        //     failure; it is not closed here.
        //   - It can stay shut. `TranslationStore.saveEdits` clears
        //     `pendingEdits` only on success, so one failed single-word save
        //     leaves that language's entry populated and every later persist for
        //     that language is skipped until some later `saveEdits` succeeds.
        //     The skip is logged below rather than being silent.
        if (!translationStore.pendingEdits.get(lang)?.size) {
          owner.onPersist?.();
        } else if (owner.onPersist) {
          logger.debug('skipped caption persist: language has pending edits', {
            lang,
            pendingCount: translationStore.pendingEdits.get(lang)?.size ?? 0,
          });
        }
      } else {
        logger.warn('caption text sync returned false after translation edit', {
          lang,
        });
      }
    } catch (error) {
      logger.error('failed to sync caption text after translation edit', {
        lang,
        ...extractErrorInfo(error),
      });
    }
  }, REPRODUCE_DEBOUNCE_MS);

  // PLAT-10862 — fire on a USER EDIT, never on a translation LOAD. Both mutate
  // the same `translations` map, so watching it naively conflated them: a
  // language switch lazily LOADS that language (a plain `translations.set`, just
  // like an edit), which silently re-textured captions a cross-tab edit had left
  // stale and hid the "Refresh Captions" notice.
  //
  // They are separable by the SHAPE of the change, without any store-side flag:
  //   - A LOAD only ever ADDS a language key — `loadTranslation` /
  //     `discoverTranslations` both bail when the language is already present.
  //   - An EDIT changes an ALREADY-PRESENT language: it replaces that language's
  //     `TranslationData` (immutable update, new reference) and/or mutates
  //     `pendingEdits` (which loads never touch).
  // So: a value-reference change on an existing translations key, or any
  // pendingEdits change, is an edit; a brand-new translations key is a load.
  //
  // The data fn snapshots the maps by VALUE IDENTITY (shallow — cheap, no deep
  // hashing) and reads every key+value so MobX re-runs it on any change; the
  // effect diffs the snapshot against the previous one. Reading the whole map
  // also avoids the old "dead reaction" trap (a language-gated read saw no
  // observables when Original and never re-ran). The language guard stays in
  // `reproduce()`.
  const snapshot = (): {
    translations: Map<string, unknown>;
    pendingEdits: Map<string, unknown>;
  } => ({
    translations: new Map<string, unknown>(translationStore.translations),
    pendingEdits: new Map<string, unknown>(translationStore.pendingEdits),
  });

  const isUserEdit = (
    current: ReturnType<typeof snapshot>,
    previous: ReturnType<typeof snapshot>,
  ): boolean => {
    // pendingEdits are only ever written by edits — any change is an edit.
    if (current.pendingEdits.size !== previous.pendingEdits.size) return true;
    for (const [lang, ref] of current.pendingEdits) {
      if (previous.pendingEdits.get(lang) !== ref) return true;
    }
    // A translations key present in BOTH but with a new value reference is an
    // in-place edit; a key present only in `current` is a fresh load.
    for (const [lang, ref] of current.translations) {
      if (
        previous.translations.has(lang) &&
        previous.translations.get(lang) !== ref
      ) {
        return true;
      }
    }
    return false;
  };

  const dispose = reaction(
    snapshot,
    (current, previous) => {
      if (isUserEdit(current, previous)) reproduce();
    },
    { equals: comparer.shallow },
  );

  // PLAT-11905 — a dubbed language's dub-timed captions link each word to its
  // translation fragment, and those links are worked out when the captions are
  // derived. So any change to a translation — a LOAD as well as an edit, unlike
  // the re-text above — derives them again at once. Nothing is re-texted or
  // saved here; on a caption track showing source-timed rows it does nothing.
  const relinkDubWords = reaction(
    () => new Map<string, unknown>(translationStore.translations),
    () => {
      if (editor.refreshDubCaptions?.()) subscribers[0]?.current.onReproduced();
    },
    { equals: comparer.shallow },
  );

  return {
    subscribers,
    teardown: () => {
      reproduce.cancel();
      dispose();
      relinkDubWords();
    },
  };
}

/**
 * Join the editor's single installation, creating it on the first subscriber
 * and tearing it down when the last one leaves. Returns the unsubscribe.
 *
 * The installation binds the `translationStore` its first subscriber brought.
 * That is the singleton every caller reads from `useTranslationStore()`.
 */
function subscribeToCaptionRebuild(
  editor: IVideoEditor,
  translationStore: TranslationStore,
  subscriber: Subscriber,
): () => void {
  let installation = installations.get(editor);
  if (!installation) {
    installation = installReaction(editor, translationStore);
    installations.set(editor, installation);
  }
  installation.subscribers.push(subscriber);

  return () => {
    const current = installations.get(editor);
    if (!current) return;
    const index = current.subscribers.indexOf(subscriber);
    if (index !== -1) current.subscribers.splice(index, 1);
    if (current.subscribers.length === 0) {
      current.teardown();
      installations.delete(editor);
    }
  };
}

/**
 * Re-produce the editor's karaoke track when a translation edit (or revert)
 * changes the language currently shown in the captions, then run the
 * surface's karaoke refresh so the rendered video captions follow.
 *
 * Why this exists: the captions/subtitles render path bakes each clip's text
 * from `TranslationPhrase.translatedValue` at *produce* time
 * (`ProduceKaraokeCommand`). `commandProduceKaraoke` only runs on explicit
 * triggers — language toggle / template apply (`applyCaptionsLanguage`). A
 * popover edit updates `TranslationStore` but nothing re-produces, so the
 * baked karaoke track (and the captions view's Tier-1 pre-produced lines)
 * keep the stale machine translation. This reaction closes that gap.
 *
 * It syncs TEXT ONLY (`commandSyncTranslatedCaptionText`) rather than
 * re-producing. A full re-produce rebuilds the track from the producer's
 * segmentation and so silently discarded every caption-STRUCTURE edit the user
 * had made — synthetic line breaks and merged rows. Merges leave no record at
 * all, so nothing could replay them; "Fix Translation" would quietly undo the
 * user's line work. The in-place sync rewrites only `translatedText` /
 * `translatedPhrases`, leaving clip boundaries, offsets and sibling groups
 * intact.
 *
 * Scope guards keep it cheap and surface-correct:
 *   - Only fires when the editor's default karaoke language is a real
 *     translation (not Original) — original-language captions don't read
 *     translations.
 *   - Only when a karaoke track already exists — nothing to refresh
 *     otherwise, and we never want to *create* a track as a side effect of
 *     an edit on an unrelated editor.
 *
 * The reaction tracks the active language's translation data + pending edits
 * (both get a fresh identity on every `editTranslatedPhrase` / `revertTranslation`
 * / `saveEdits`), so it fires for edits made on any surface sharing the
 * singleton store — matching the cross-surface propagation contract.
 *
 * `onReproduced` is the surface's observable refresh:
 *   - Creator Studio: `editorStore.refreshKaraoke()`
 *   - Clip preview:   `editorStore.refreshKaraokeState()`
 *
 * `onPersist` is the surface's SAVE. PLAT-10945: refreshing is not enough.
 * The rebuilt track lives in the editor and in the karaoke store; nothing
 * writes it to storage, so a reload re-reads the pre-correction track and the
 * user's correction is gone from the captions view while it is still in the
 * prose view (proven in the running application on 2026-07-30: one correction,
 * two re-produces, zero `karaokeData` writes). Each host passes its own
 * existing save entry point — `saveCurrentAndParentVideos`.
 *
 * Mounting this hook many times against ONE editor is safe and cheap: they all
 * share that editor's single reaction (see `installations` above), so one
 * translation change costs one re-produce and one save no matter how many of
 * the five story-level hosts happen to be mounted.
 */
export function useReproduceCaptionsOnTranslationEdit(
  editor: IVideoEditor | null | undefined,
  translationStore: TranslationStore,
  onReproduced: () => void,
  onPersist?: () => void,
): void {
  // The shared reaction reads the callbacks through this ref. Keeping them out
  // of the effect's dependencies means a caller whose callbacks change identity
  // on re-render does not tear down and reinstall the editor's reaction (which
  // would cancel a re-produce already waiting in the debounce window).
  const handlers = useRef<Handlers>({ onReproduced, onPersist });
  useEffect(() => {
    handlers.current = { onReproduced, onPersist };
  });

  useEffect(() => {
    if (!editor) return;
    return subscribeToCaptionRebuild(editor, translationStore, handlers);
  }, [editor, translationStore]);
}
